// Patient DTOs. A patient is the person a case treats; a per-case patient portal account
// (users.case_id) lets them see their case and submit media, which a doctor-reviewer approves.
// Media kinds include clinical scan/model/STL artifacts (aligner intake), not just uploads.
import { z } from 'zod';

export const PATIENT_SEX = ['male', 'female', 'other'] as const;
export type PatientSex = (typeof PATIENT_SEX)[number];

export const MEDIA_KINDS = ['image', 'video', 'document', 'scan', 'model', 'stl'] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];
export const MEDIA_KIND_LABELS: Record<MediaKind, string> = {
  image: 'Image',
  video: 'Video',
  document: 'Document',
  scan: 'Intraoral scan',
  model: 'Model',
  stl: 'STL',
};

export const MEDIA_REVIEW_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type MediaReviewStatus = (typeof MEDIA_REVIEW_STATUSES)[number];
export const MEDIA_REVIEW_LABELS: Record<MediaReviewStatus, string> = {
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
};

export interface Patient {
  id: string;
  patientCode: string;
  clinicId: string | null;
  fullName: string;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  dateOfBirth: string | null;
  sex: PatientSex | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PatientMedia {
  id: string;
  caseId: string;
  patientId: string | null;
  uploadedByUserId: string | null;
  kind: MediaKind;
  filePath: string;
  /** Original filename as uploaded (used as the download name); null for legacy free-text refs. */
  originalName: string | null;
  /** Content-type recorded at upload; null for legacy refs. */
  mimeType: string | null;
  /** Stored byte size; null for legacy refs. */
  sizeBytes: number | null;
  /** Whether this file is shared to the clinic/patient portal. */
  clientVisible: boolean;
  /** Optional grouping tag (e.g. 'Doctor files', 'Scans'). */
  category: string | null;
  caption: string | null;
  status: MediaReviewStatus;
  reviewNote: string | null;
  reviewedByUserId: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

const optText = (max: number) => z.string().trim().max(max).nullish();

export const createPatientSchema = z.object({
  fullName: z.string().trim().min(1).max(160),
  clinicId: z.string().min(1).nullish(),
  phone: optText(40),
  whatsapp: optText(40),
  email: z.string().trim().email().max(160).nullish(),
  dateOfBirth: z.string().min(1).nullish(), // ISO date
  sex: z.enum(PATIENT_SEX).nullish(),
  notes: optText(2000),
});
export type CreatePatientRequest = z.infer<typeof createPatientSchema>;

export const updatePatientSchema = z
  .object({
    fullName: z.string().trim().min(1).max(160).optional(),
    clinicId: z.string().min(1).nullish(),
    phone: optText(40),
    whatsapp: optText(40),
    email: z.string().trim().email().max(160).nullish(),
    dateOfBirth: z.string().min(1).nullish(),
    sex: z.enum(PATIENT_SEX).nullish(),
    notes: optText(2000),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type UpdatePatientRequest = z.infer<typeof updatePatientSchema>;

export interface PatientListQuery {
  search?: string;
  clinicId?: string;
  limit: number;
  offset: number;
}

/** Provision a per-case patient portal login (role 'patient', users.case_id set). */
export const patientPortalAccessSchema = z.object({
  caseId: z.string().min(1),
  username: z.string().trim().min(3).max(60),
  password: z.string().min(8).max(200),
  displayName: z.string().trim().min(1).max(120),
});
export type PatientPortalAccessRequest = z.infer<typeof patientPortalAccessSchema>;

/** Submit a media artifact against a case (patient upload or staff/clinical intake). */
export const submitMediaSchema = z.object({
  kind: z.enum(MEDIA_KINDS),
  filePath: z.string().trim().min(1).max(500),
  caption: optText(500),
  patientId: z.string().min(1).nullish(),
});
export type SubmitMediaRequest = z.infer<typeof submitMediaSchema>;

/** Doctor-reviewer approve/reject of submitted media. */
export const reviewMediaSchema = z.object({
  status: z.enum(['approved', 'rejected']),
  reviewNote: optText(500),
});
export type ReviewMediaRequest = z.infer<typeof reviewMediaSchema>;
